import React, { createContext, ReactNode, useState } from 'react';
import * as vp from 'vopay';

export type PaymentMethod = {
  method: vp.ConnectionTypes;
  token: string;
  amount: number;
  account: string;
  FinancialInstitution?: {
    FinancialInstitutionID: string;
    FinancialInstitutionNumber: string;
    FIName: string;
    FINameFrench: string;
    ColorCode: string;
    VIOFinancialInstitutionsFiserv_ID: string;
    Country: string;
    ExternalFIID: string;
    OnlineConnectionEnabled: string;
    AssociatedFINames: string;
    AssociatedFINamesFrench: string;
    AssociatedExternalFIIDs: string;
    HasMoreThanOne: boolean;
  };
};

export type FlexiblePaymentCtx = {
  isFlexiblePayment: boolean;
  paymentType?: vp.FlexiblePaymentMethods;
  paymentMethods: PaymentMethod[];
  partialPaymentValues: vp.PartialPayment;
  installmentDetails: vp.InstallmentDetails;
  updateFlexiblePayment: (isFlexible: boolean, type: vp.FlexiblePaymentMethods) => void;
  removePaymentMethod: (method: vp.ConnectionTypes) => void;
  addPaymentMethod: (paymentMethod: PaymentMethod) => void;
  updatePartialPaymentValues: (values: vp.PartialPayment) => void;
  updateInstallmentDetails: (values: vp.InstallmentDetails) => void;
  resetPaymentMethod: () => void;
};

// Define the initial state
const initialFlexiblePayments: FlexiblePaymentCtx = {
  isFlexiblePayment: false,
  paymentType: undefined,
  paymentMethods: [],
  partialPaymentValues: {
    Amount: '',
    RemainderPaymentDate: null,
  },
  installmentDetails: {
    initialAmount: '',
    scheduleAmount: '',
    scheduleStartDate: '',
  },
  addPaymentMethod: () => {},
  removePaymentMethod: () => {},
  updateFlexiblePayment: () => {},
  updatePartialPaymentValues: () => {},
  updateInstallmentDetails: () => {},
  resetPaymentMethod: () => {},
};

// Create the context
export const FlexiblePaymentContext = createContext(initialFlexiblePayments);

// Create the provider component
export const FlexiblePaymentProvider = ({ children }: { children: ReactNode }) => {
  const [isFlexiblePayment, setIsFlexiblePayment] = useState(false);
  const [paymentType, setPaymentType] = useState<vp.FlexiblePaymentMethods>();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [partialPaymentValues, setPartialPaymentValues] = useState<vp.PartialPayment>({
    Amount: '',
    RemainderPaymentDate: '',
  });
  const [installmentDetails, setInstallmentDetails] = useState<vp.InstallmentDetails>({
    initialAmount: '',
    scheduleAmount: '',
    scheduleStartDate: '',
  });

  // Function to update the flexible payment status
  const updateFlexiblePayment = (isFlexible: boolean, type: vp.FlexiblePaymentMethods) => {
    setIsFlexiblePayment(isFlexible);
    setPaymentType(type);
  };

  // Function to add a payment method
  const addPaymentMethod = (paymentMethod: PaymentMethod) => {
    setPaymentMethods((prevMethods) => [
      ...prevMethods.filter((method) => method.method !== paymentMethod.method),
      paymentMethod,
    ]);
  };
  const removePaymentMethod = (method: vp.ConnectionTypes) => {
    setPaymentMethods((prevMethods) => prevMethods.filter((m) => m.method !== method));
  };
  const resetPaymentMethod = () => {
    setPaymentMethods([]);
  };
  return (
    <FlexiblePaymentContext.Provider
      value={{
        isFlexiblePayment,
        paymentType,
        paymentMethods,
        partialPaymentValues,
        installmentDetails,
        updateFlexiblePayment,
        removePaymentMethod,
        addPaymentMethod,
        updatePartialPaymentValues: setPartialPaymentValues,
        updateInstallmentDetails: setInstallmentDetails,
        resetPaymentMethod,
      }}
    >
      {children}
    </FlexiblePaymentContext.Provider>
  );
};
export const useFlexiblePayment = (): FlexiblePaymentCtx => React.useContext(FlexiblePaymentContext);
